import React, { useEffect, useState } from "react";
import * as BosWebGuiServices from "data/BosWebServices/BosWebGuiServices";
import ContentLoadingBOS from "components/ContentLoadingBos";
import Viewport from "../Viewport/Viewport";
import { CLOSE_VIEWPORT_ACTION } from "../../client20/logic/Constants";
import { toEM } from "../../client20/logic/WidgetUtils";

// ViewModes
import { TaskView } from "./ViewModes/TaskView";
import { FullView } from "./ViewModes/FullView";
import { SlideView } from "./ViewModes/SlideView";

// DevExtreme components
import notify from 'devextreme/ui/notify';

// Redux
import { connect } from "react-redux";

function SessionView(props) {
  const { availableSpace, dataSegment, session, appIdle, taskLayout, onCloseSession, updateViewportList } = props;

  const NOTIFY_TIME_IN_MS = 3000;
  const anyChangesInterval = 1000 * 10;  // 10 seconds

  const [reloadViewportTrigger, setReloadViewportTrigger] = useState(0);

  const [viewports, setViewports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const viewportMode = session.isWorkspace ? (taskLayout === "experimental" ? "SLIDE" : "TASK") : null;

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        let asw = toEM(availableSpace.width);
        let ash = toEM(availableSpace.height);
        console.log("GetViewports triggered with sessionId and size", session.id, asw, ash);
        let result = await BosWebGuiServices.fetchGetViewports(dataSegment.authToken, session.id, asw, ash);
        console.log("GetViewports result: ", result);
        if (result.noChanges) return;
        let resultViewports = (result && result.viewports) ? result.viewports : [];
        setViewports(resultViewports);
      } catch (error) {
        console.error("GetViewports error: ", error);
        setViewports([]);
      }
      setIsLoading(false);
    }

    if (session.id && dataSegment.authToken) {
      fetchData();
    }
    else {
      setViewports([]);
    }
  }, [reloadViewportTrigger, session, dataSegment.authToken]);

  useEffect(() => {
    if (appIdle) {
      console.log("No AnyChanges because app is idle");
      return;
    }

    //one anychanges now
    anyChanges();

    //anychanges interval
    const intervalHandle = setInterval(() => {
      anyChanges();
    }, anyChangesInterval);
    console.log("Started AnyChanges Interval", intervalHandle);

    //cleanup
    return () => {
      clearInterval(intervalHandle);
      console.log("Stopped AnyChanges Interval", intervalHandle);
    };
  }, [session, dataSegment, viewports.length, appIdle]);

  const anyChanges = () => {
    //if (viewports.length === 0) return;
    console.log("AnyChanges triggered", session.id);
    BosWebGuiServices.fetchAnyChanges(dataSegment.authToken, session.id)
      .then(result => {
        console.log("AnyChanges result: ", result);
        handleResult(result);
      })
      .catch(error => {
        console.error("AnyChanges result: ", error);
        if (error.message && error.message.includes("Session recovered!")) {
          handleSessionRecovered();
        }
      });
  };

  useEffect(() => {
    let list = viewports.map((sv) => ({ displayName: sv.displayName, id: sv.viewportId }));
    updateViewportList(list);
  }, [JSON.stringify(viewports.map(vp => vp.viewportId))]);

  const handleSessionRecovered = () => {
    console.info("Viewport Reload because Session was recovered");
    notify("Session was recovered!", "info", NOTIFY_TIME_IN_MS);
    handleReloadViewport();
  };

  const handleCloseViewport = async (viewportId) => {
    console.log("DoAction triggered");
    return await BosWebGuiServices.fetchDoAction(dataSegment.authToken, session.id, viewportId, "NULL", CLOSE_VIEWPORT_ACTION, "NULL")
      .then(result => {
        console.log("DoAction result: ", result);
        handleResult(result);
        return true;
      })
      .catch(error => {
        console.error("DoAction result: ", error);
        return false;
      });
  };

  const handleCloseSession = () => {
    onCloseSession();
  };

  const toNotifyType = (userMessageType) => {
    switch (userMessageType) {
      case "UserInfo":
        return "info";
      case "UserWarning":
        return "warning";
      default:
        return "error";
    }
  }

  const handleResult = (result) => {

    let newTargetUri = result && result.navigationUri ? result.navigationUri : null;
    if (newTargetUri) {
      let url = session.isWorkspace ? "/workspace/" + newTargetUri : newTargetUri;
      props.history.push(url);
      return;
    }

    if (result && result.message) {
      let type = toNotifyType(result.messageType);
      notify(result.message, type, NOTIFY_TIME_IN_MS);
    }

    if (result && result.modifications) {
      //console.log("AllModifications", result.modifications);
      let nonEmptyModifications = result.modifications.filter(m => m.data || m.uiModifications);
      //console.log("NonEmptyModifications", nonEmptyModifications);
      if (nonEmptyModifications.length > 0)
        setViewports(prev => mergeViewports([...prev], nonEmptyModifications));
    }

    let newViewport = result && result.newViewport ? result.newViewport : null;
    if (newViewport) {
      setViewports(prev => [...prev, newViewport]);
    }

    let closeViewportRef = result && result.closeViewportRef ? result.closeViewportRef : null;
    if (closeViewportRef) {
      if (viewports.length <= 1) handleCloseSession();
      else if (viewports[0].viewportId === closeViewportRef) handleCloseSession();
      else setViewports(prev => prev.filter(vp => vp.viewportId !== closeViewportRef));
    }
  };

  const mergeViewports = (prevViewports, modifications) => {
    for (let viewport of modifications) {
      let id = viewport.viewportRef;
      let newUi = viewport.uiModifications;
      let newData = viewport.data;
      let newWidth = viewport.width;
      let newHeight = viewport.height;
      prevViewports = prevViewports.map(vp => (vp.viewportId === id) ? {
        ...vp,
        rootWidget: newUi ? mergeViewportUi({ ...vp.rootWidget }, newUi) : vp.rootWidget,
        data: newData ? mergeViewportData([...vp.data], newData) : vp.data,
        width: newWidth ? newWidth : vp.width,
        height: newHeight ? newHeight : vp.height
      }
        : vp);
    }
    return prevViewports;
  }

  const mergeViewportData = (prevData, newData) => {
    for (let i = 0; i < newData.length; i++) {
      let found = false;
      for (let j = 0; j < prevData.length; j++) {
        if (prevData[j].widgetRef === newData[i].widgetRef) {
          prevData[j] = newData[i];
          found = true;
          break;
        }
      }
      if (!found) prevData.push(newData[i]);
    }
    return prevData;
  };

  const mergeViewportUi = (prevWidget, newUiDefinitions) => {
    for (let i = 0; i < newUiDefinitions.length; i++) {
      if (prevWidget.id === newUiDefinitions[i].id) {
        prevWidget = newUiDefinitions[i];
        continue;
      }
      if (prevWidget.containingWidgets) {
        for (let j = 0; j < prevWidget.containingWidgets.length; j++) {
          prevWidget.containingWidgets[j] = mergeViewportUi(prevWidget.containingWidgets[j], [newUiDefinitions[i]]);
        }
      }
    }
    return prevWidget;
  };

  const handleReloadViewport = () => {
    setReloadViewportTrigger(prev => prev + 1);
  };

  const renderViewport = (viewport, isEnabled) => {
    return (
      <Viewport
        sessionId={session.id}
        dataSegment={dataSegment}
        viewport={viewport}
        isEnabled={isEnabled}
        onResult={handleResult}
        {...props} />
    );
  }

  const renderViewMode = () => {
    switch (viewportMode) {
      case "TASK":
        return (
          <TaskView viewports={viewports} renderViewport={renderViewport} closeViewport={handleCloseViewport} reloadViewport={handleReloadViewport} {...props} />
        );
      case "SLIDE":
        return (
          <SlideView viewports={viewports} renderViewport={renderViewport} closeViewport={handleCloseViewport} {...props} />
        );
      default:
        return (
          <FullView viewports={viewports} renderViewport={renderViewport} {...props} />
        );
    }
  };

  return isLoading ? (
    <ContentLoadingBOS />
  ) : (
    session.taskId
      ? renderViewMode()
      : <React.Fragment />
  );
}

function mapStateToProps(state, ownProps) {
  return {
    appIdle: state.appState.appIdle,
    taskLayout: state.appSettings.taskLayout,
  };
}

export default
  connect(
    mapStateToProps
  )(SessionView);
